/**
 * Biblioteca CANÔNICA do Dossiê Comercial (determinística, sem IA por Candidate): para cada código da taxonomia semântica
 * (`semanticTaxonomy.js`) há um rótulo, o motivo de aplicabilidade, um ponto de partida ("Explorar como...") e perguntas abertas.
 * Nada aqui afirma que a empresa tenha uma necessidade, dor ou falha: o fato publicado abre a conversa, a pergunta valida.
 */
export default {
  capabilityLabels: {
    sells_products: "Vende produtos",
    sells_services: "Vende serviços",
    serves_business_customers: "Atende empresas",
    serves_consumers: "Atende consumidores",
    has_customer_base: "Tem base de clientes",
    runs_sales_operation: "Tem operação comercial",
    lead_generation: "Busca novos clientes",
    partner_channels: "Trabalha com parceiros",
    customer_support_operation: "Mantém atendimento ao cliente",
    ecommerce: "Vende pela internet",
    subscription_business: "Opera por assinatura",
    field_operations: "Tem operação em campo",
    logistics_operation: "Opera logística",
    manufacturing_operation: "Tem produção própria",
    project_delivery: "Entrega por projetos",
    multi_unit_operation: "Opera em várias unidades",
    regulated_operation: "Atua em área regulada",
    training_delivery: "Oferece treinamentos",
    software_development: "Desenvolve software",
    software_product_vendor: "Vende produto de software",
    it_infrastructure: "Tem infraestrutura de TI",
    data_analytics: "Trabalha com dados",
    system_integration: "Integra sistemas",
    iot_hardware: "Trabalha com equipamentos conectados",
    cybersecurity: "Atua com segurança digital",
    digital_presence: "Tem presença digital",
    digital_transformation_project: "Divulga projeto de transformação digital",
    new_product_or_launch: "Divulga lançamento",
    expansion_or_new_unit: "Divulga expansão ou nova unidade",
    hiring_for_function: "Contrata para uma função",
    legacy_modernization: "Divulga modernização de sistemas",
    compliance_or_certification: "Divulga certificação ou conformidade",
    process_organization_gap: "Divulga reorganização de processos",
    partnership_or_investment: "Divulga parceria ou investimento",
    event_participation: "Participa de eventos",
    generic: "Informação publicada pela empresa",
  },
  capabilityReasons: {
    contextual: "Contexto de operação compatível com a oferta.",
    relevant: "Operação, projeto ou estrutura específica e pertinente à oferta.",
    strong: "Necessidade comercial específica e documentada para a oferta.",
    decisive: "Intenção atual e atribuível da empresa de contratar o que a oferta entrega.",
    sells_products: "A empresa publica produtos que vende, o que sustenta ofertas ligadas à operação de venda de produtos.",
    sells_services: "A empresa publica serviços que presta, o que sustenta ofertas ligadas à operação de serviços.",
    serves_business_customers: "A empresa declara atender outras empresas, o que sustenta ofertas voltadas a relacionamento B2B.",
    serves_consumers: "A empresa declara atender consumidores finais, o que sustenta ofertas voltadas a relacionamento com o cliente final.",
    has_customer_base: "A empresa cita clientes ou cases, o que mostra uma base de clientes em operação.",
    runs_sales_operation: "A empresa mostra uma operação comercial ativa, o que sustenta ofertas ligadas a vendas.",
    lead_generation: "A empresa divulga ações para atrair novos clientes, o que sustenta ofertas ligadas à prospecção.",
    partner_channels: "A empresa cita parceiros ou canal de revenda, o que sustenta ofertas ligadas a canais.",
    customer_support_operation: "A empresa mostra atendimento ou suporte a clientes, o que sustenta ofertas ligadas a atendimento.",
    ecommerce: "A empresa tem venda online publicada, o que sustenta ofertas ligadas a comércio digital.",
    subscription_business: "A empresa oferece planos ou assinaturas, o que sustenta ofertas ligadas a receita recorrente.",
    field_operations: "A empresa descreve trabalho em campo ou em unidades dos clientes, o que sustenta ofertas ligadas a operações externas.",
    logistics_operation: "A empresa descreve transporte, entrega ou armazenagem, o que sustenta ofertas ligadas a logística.",
    manufacturing_operation: "A empresa descreve produção ou fabricação própria, o que sustenta ofertas ligadas a operações industriais.",
    project_delivery: "A empresa descreve trabalho organizado em projetos, o que sustenta ofertas ligadas a gestão de projetos.",
    multi_unit_operation: "A empresa informa mais de uma unidade ou filial, o que sustenta ofertas ligadas a operação multiunidade.",
    regulated_operation: "A empresa atua em área com regras ou certificações, o que sustenta ofertas ligadas a conformidade.",
    training_delivery: "A empresa oferece cursos ou treinamentos, o que sustenta ofertas ligadas a educação e capacitação.",
    software_development: "A empresa publica desenvolvimento de software, o que sustenta ofertas técnicas para times de tecnologia.",
    software_product_vendor: "A empresa publica um produto de software próprio, o que sustenta ofertas ligadas a produtos digitais.",
    it_infrastructure: "A empresa descreve infraestrutura ou operação de TI, o que sustenta ofertas ligadas a tecnologia.",
    data_analytics: "A empresa publica trabalho com dados ou análises, o que sustenta ofertas ligadas a informação para decisão.",
    system_integration: "A empresa cita integrações entre sistemas, o que sustenta ofertas ligadas a integração.",
    iot_hardware: "A empresa descreve equipamentos ou dispositivos conectados, o que sustenta ofertas ligadas a IoT.",
    cybersecurity: "A empresa cita segurança da informação, o que sustenta ofertas ligadas a proteção de dados.",
    digital_presence: "A empresa mantém site e canais digitais, o que sustenta ofertas ligadas à presença online.",
    digital_transformation_project: "A empresa divulgou um projeto de transformação digital, o que é um gatilho concreto para ofertas de serviço.",
    new_product_or_launch: "A empresa divulgou um lançamento ou novo produto, o que é um gatilho concreto para ofertas de serviço.",
    expansion_or_new_unit: "A empresa divulgou expansão ou nova unidade, o que é um gatilho concreto para ofertas de serviço.",
    hiring_for_function: "A empresa tem vaga aberta relacionada à área da oferta, o que é um gatilho concreto para ofertas de serviço.",
    legacy_modernization: "A empresa divulgou modernização de sistemas ou processos, o que é um gatilho concreto para ofertas de serviço.",
    compliance_or_certification: "A empresa divulgou certificação ou adequação a normas, o que é um gatilho concreto para ofertas de serviço.",
    process_organization_gap: "A empresa divulgou reorganização de processos, o que é um gatilho concreto para ofertas de serviço.",
    partnership_or_investment: "A empresa divulgou parceria ou investimento, o que é um gatilho concreto para ofertas de serviço.",
    event_participation: "A empresa divulgou participação em evento, o que é um gatilho concreto para ofertas de serviço.",
    generic: "Informação pública da empresa aplicável à oferta.",
  },
  talkingPoints: {
    sells_products: "Explorar como a empresa vende e entrega os produtos que publica.",
    sells_services: "Explorar como a empresa organiza a prestação dos serviços que publica.",
    serves_business_customers: "Explorar como a empresa se relaciona com as empresas que atende.",
    serves_consumers: "Explorar como a empresa atende e acompanha seus consumidores.",
    has_customer_base: "Explorar como a empresa cuida da base de clientes que já cita publicamente.",
    runs_sales_operation: "Explorar como a equipe comercial da empresa organiza o seu trabalho.",
    lead_generation: "Explorar como a empresa busca novos clientes hoje.",
    partner_channels: "Explorar como a empresa trabalha com parceiros e revendas.",
    customer_support_operation: "Explorar como a empresa organiza o atendimento aos clientes.",
    ecommerce: "Explorar como funciona a venda online da empresa.",
    subscription_business: "Explorar como a empresa gerencia planos e assinantes.",
    field_operations: "Explorar como a empresa organiza as equipes que trabalham em campo.",
    logistics_operation: "Explorar como a empresa acompanha entregas e movimentações.",
    manufacturing_operation: "Explorar como a empresa organiza a produção que declara ter.",
    project_delivery: "Explorar como a empresa acompanha seus projetos.",
    multi_unit_operation: "Explorar como a empresa acompanha as suas várias unidades.",
    regulated_operation: "Explorar como a empresa lida com as exigências da área em que atua.",
    training_delivery: "Explorar como a empresa organiza cursos e treinamentos.",
    software_development: "Explorar como a empresa organiza o desenvolvimento de software.",
    software_product_vendor: "Explorar como a empresa evolui e entrega o produto de software que publica.",
    it_infrastructure: "Explorar como a empresa mantém a sua infraestrutura de TI.",
    data_analytics: "Explorar como a empresa usa dados nas suas decisões.",
    system_integration: "Explorar como a empresa conecta os sistemas que usa.",
    iot_hardware: "Explorar como a empresa acompanha os equipamentos que declara.",
    cybersecurity: "Explorar como a empresa trata segurança da informação.",
    digital_presence: "Explorar como a empresa usa seus canais digitais para falar com o mercado.",
    digital_transformation_project: "Explorar como o projeto de transformação digital divulgado está sendo conduzido.",
    new_product_or_launch: "Explorar como o lançamento divulgado está sendo preparado.",
    expansion_or_new_unit: "Explorar como a expansão divulgada está sendo organizada.",
    hiring_for_function: "Explorar o que levou a empresa a abrir a vaga publicada.",
    legacy_modernization: "Explorar como a modernização divulgada está sendo conduzida.",
    compliance_or_certification: "Explorar como a empresa conduz a certificação ou adequação divulgada.",
    process_organization_gap: "Explorar como a reorganização divulgada está sendo feita.",
    partnership_or_investment: "Explorar o que a parceria ou o investimento divulgado muda na operação.",
    event_participation: "Explorar o que a empresa busca com a participação em eventos.",
    generic: "Explorar o que a empresa publica e como isso se conecta com a oferta.",
  },
  questionLibrary: {
    'sells_products.1': "Como os produtos chegam hoje aos clientes?",
    'sells_products.2': "Quais produtos têm mais peso na operação?",
    'sells_products.3': "Quem acompanha a venda de cada produto?",
    'sells_services.1': "Como os serviços são contratados e acompanhados?",
    'sells_services.2': "Quais serviços concentram a maior parte da operação?",
    'sells_services.3': "Quem responde pela entrega de cada serviço?",
    'serves_business_customers.1': "Que perfil de empresa costuma contratar vocês?",
    'serves_business_customers.2': "Como é o ciclo de uma contratação típica?",
    'serves_business_customers.3': "Como vocês acompanham as empresas depois de contratar?",
    'serves_consumers.1': "Por quais canais o consumidor fala com vocês?",
    'serves_consumers.2': "Como vocês acompanham a experiência depois da compra?",
    'serves_consumers.3': "O que mais gera retorno de clientes?",
    'has_customer_base.1': "Como vocês organizam as informações dos clientes atuais?",
    'has_customer_base.2': "Como acompanham a relação com os clientes ao longo do tempo?",
    'has_customer_base.3': "O que fazem com clientes que ficaram inativos?",
    'runs_sales_operation.1': "Como a equipe comercial acompanha oportunidades hoje?",
    'runs_sales_operation.2': "Como é o caminho de um contato até a venda?",
    'runs_sales_operation.3': "Que informação o time gostaria de ter à mão na conversa?",
    'lead_generation.1': "Como novos clientes chegam até vocês?",
    'lead_generation.2': "Que canais trazem os contatos mais qualificados?",
    'lead_generation.3': "Como decidem quem abordar primeiro?",
    'partner_channels.1': "Como vocês acompanham o trabalho dos parceiros?",
    'partner_channels.2': "Como um novo parceiro é apresentado e ativado?",
    'partner_channels.3': "O que parceiros mais pedem a vocês?",
    'customer_support_operation.1': "Por quais canais os clientes pedem ajuda?",
    'customer_support_operation.2': "Como as solicitações são registradas e acompanhadas?",
    'customer_support_operation.3': "Que tipo de pedido mais se repete?",
    'ecommerce.1': "Como os pedidos online são tratados internamente?",
    'ecommerce.2': "Quais plataformas ou canais online vocês usam?",
    'ecommerce.3': "Como acompanham os resultados da venda online?",
    'subscription_business.1': "Como os planos e renovações são acompanhados?",
    'subscription_business.2': "Como identificam quem pode cancelar ou trocar de plano?",
    'subscription_business.3': "Que informações usam para decidir mudanças de preço?",
    'field_operations.1': "Como as equipes em campo recebem e registram o trabalho?",
    'field_operations.2': "Como vocês acompanham o que acontece fora da sede?",
    'field_operations.3': "Que informação do campo mais falta no escritório?",
    'logistics_operation.1': "Como acompanham uma entrega do início ao fim?",
    'logistics_operation.2': "Como o cliente é avisado sobre o andamento?",
    'logistics_operation.3': "Que etapa da operação dá mais trabalho de controlar?",
    'manufacturing_operation.1': "Como o plano de produção é definido e acompanhado?",
    'manufacturing_operation.2': "Como a produção se conecta com vendas e estoque?",
    'manufacturing_operation.3': "Que etapa da produção mais exige acompanhamento?",
    'project_delivery.1': "Como um projeto é planejado e acompanhado?",
    'project_delivery.2': "Como o cliente recebe atualizações do projeto?",
    'project_delivery.3': "O que mais costuma mudar durante um projeto?",
    'multi_unit_operation.1': "Como as informações das unidades chegam à matriz?",
    'multi_unit_operation.2': "Como padronizam o trabalho entre unidades?",
    'multi_unit_operation.3': "Que indicador compara as unidades hoje?",
    'regulated_operation.1': "Como acompanham prazos e exigências da área?",
    'regulated_operation.2': "Como guardam os registros para uma auditoria?",
    'regulated_operation.3': "Quem cuida das certificações hoje?",
    'training_delivery.1': "Como as turmas e inscrições são gerenciadas?",
    'training_delivery.2': "Como acompanham o aprendizado dos participantes?",
    'training_delivery.3': "Como divulgam novas turmas?",
    'software_development.1': "Como o time de tecnologia organiza o trabalho hoje?",
    'software_development.2': "Que ferramentas o time usa no dia a dia?",
    'software_development.3': "Como priorizam o que desenvolver primeiro?",
    'software_product_vendor.1': "Como novos clientes começam a usar o produto?",
    'software_product_vendor.2': "Como organizam o suporte aos usuários do produto?",
    'software_product_vendor.3': "O que mais pesa nas decisões do roadmap?",
    'it_infrastructure.1': "Como a infraestrutura de TI é mantida e monitorada?",
    'it_infrastructure.2': "Que partes do ambiente são próprias e quais são contratadas?",
    'it_infrastructure.3': "Como tratam incidentes quando acontecem?",
    'data_analytics.1': "Como os dados da empresa são reunidos hoje?",
    'data_analytics.2': "Que decisões mais dependem de relatórios?",
    'data_analytics.3': "Quem consome essas informações?",
    'system_integration.1': "Que sistemas precisam conversar entre si hoje?",
    'system_integration.2': "Como as informações passam de um sistema para outro?",
    'system_integration.3': "O que mais se repete manualmente entre sistemas?",
    'iot_hardware.1': "Como acompanham o funcionamento dos equipamentos?",
    'iot_hardware.2': "Como os dados dos equipamentos são usados?",
    'iot_hardware.3': "Que parte da operação depende desses dispositivos?",
    'cybersecurity.1': "Como cuidam do acesso às informações sensíveis?",
    'cybersecurity.2': "Que exigências de segurança os clientes fazem a vocês?",
    'cybersecurity.3': "Como acompanham ameaças e incidentes?",
    'digital_presence.1': "Que canais digitais trazem mais contatos?",
    'digital_presence.2': "Como o site é atualizado e por quem?",
    'digital_presence.3': "Como medem o resultado desses canais?",
    'digital_transformation_project.1': "Em que etapa está o projeto divulgado?",
    'digital_transformation_project.2': "Quem conduz o projeto dentro da empresa?",
    'digital_transformation_project.3': "Que partes do projeto ainda estão em aberto?",
    'new_product_or_launch.1': "O que o lançamento exige da operação?",
    'new_product_or_launch.2': "Quem acompanha o lançamento?",
    'new_product_or_launch.3': "Que prazo está previsto?",
    'expansion_or_new_unit.1': "Como a nova unidade vai se conectar com a operação atual?",
    'expansion_or_new_unit.2': "Quem cuida da implantação?",
    'expansion_or_new_unit.3': "Que prazo está previsto para a abertura?",
    'hiring_for_function.1': "O que a vaga publicada pretende resolver?",
    'hiring_for_function.2': "A função já existia ou é nova?",
    'hiring_for_function.3': "Como o trabalho dessa função é apoiado hoje?",
    'legacy_modernization.1': "O que está sendo modernizado e por quê?",
    'legacy_modernization.2': "Que sistemas continuam em uso durante a mudança?",
    'legacy_modernization.3': "Quem decide as prioridades da modernização?",
    'compliance_or_certification.1': "Que certificação ou norma está em andamento?",
    'compliance_or_certification.2': "Quem acompanha as exigências no dia a dia?",
    'compliance_or_certification.3': "Que prazo está previsto?",
    'process_organization_gap.1': "Que processos estão sendo reorganizados?",
    'process_organization_gap.2': "Quem conduz a reorganização?",
    'process_organization_gap.3': "Como vão medir se deu certo?",
    'partnership_or_investment.1': "O que a parceria ou o investimento muda na prática?",
    'partnership_or_investment.2': "Que áreas devem receber mais atenção?",
    'partnership_or_investment.3': "Que prazo está previsto?",
    'event_participation.1': "O que a empresa busca nesse tipo de evento?",
    'event_participation.2': "Como acompanham os contatos feitos nos eventos?",
    'event_participation.3': "Que eventos estão previstos?",
    'generic.1': "Como a empresa organiza hoje o trabalho descrito no que publica?",
    'generic.2': "Quem acompanha essa área na empresa?",
    'generic.3': "O que mais pesa nessa área atualmente?",
  },
};
